import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { apiGET, apiPOST } from "../../utilities/apiHelpers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ButtonWithLoader from "../Button/ButtonWithLoader";
import { Link } from "react-router-dom";

/* ================= VALIDATION REGEX ================= */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^[6-9]\d{9}$/; // India mobile numbers

function EditProfile() {
  const users = useSelector((state) => state?.user?.userData?.id);

  const [loading, setLoading] = useState(false);
  const [userDetails, setUserData] = useState({
    name: "",
    email: "",
    phoneNo: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phoneNo: "",
  });

  /* ================= FETCH USER ================= */
  const fetchUserData = useCallback(async () => {
    if (!users) return;

    try {
      const response = await apiGET(`v1/users/${users}`);
      const {
        name = "",
        email = "",
        phoneNo = "",
      } = response?.data?.data?.data || {};

      setUserData({ name, email, phoneNo });
    } catch (error) {
      console.error(error);
      toast.error("Failed to load profile");
    }
  }, [users]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    const newErrors = {};

    // Name
    if (!userDetails.name.trim()) {
      newErrors.name = "Name is required";
    } else if (userDetails.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(userDetails.name)) {
      newErrors.name = "Name can contain only letters";
    }

    // Email
    if (!userDetails.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!EMAIL_REGEX.test(userDetails.email)) {
      newErrors.email = "Invalid email address";
    }

    // Phone
    if (!userDetails.phoneNo.trim()) {
      newErrors.phoneNo = "Phone number is required";
    } else if (!PHONE_REGEX.test(userDetails.phoneNo)) {
      newErrors.phoneNo = "Enter valid 10-digit mobile number";
    }

    setErrors({
      name: newErrors.name || "",
      email: newErrors.email || "",
      phoneNo: newErrors.phoneNo || "",
    });

    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors)[0]);
      return false;
    }

    return true;
  };

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const response = await apiPOST("v1/users/update-user", userDetails);

      if (response?.status === 200) {
        toast.success("Profile updated successfully");
      } else {
        toast.error("Update failed");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-[793px]">
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          <div className="flex flex-wrap -mx-4">
            {/* NAME */}
            <div className="flex-1 px-4">
              <label className="block text-gray-700 text-xs">Full name</label>
              <input
                type="text"
                name="name"
                value={userDetails.name}
                onChange={handleChange}
                placeholder="Enter full name"
                className={`mt-1 block w-full p-3 text-xs outline-none rounded bg-[#F8F8F8]
                  ${errors.name ? "border border-red-500" : "border border-gray-300"}`}
              />
              {errors.name && (
                <p className="text-red-500 text-[10px] mt-1">{errors.name}</p>
              )}
            </div>

            {/* EMAIL */}
            <div className="flex-1 px-4">
              <label className="block text-gray-700 text-xs">
                E-mail address
              </label>
              <input
                type="email"
                name="email"
                value={userDetails.email}
                onChange={handleChange}
                placeholder="Enter email address"
                className={`mt-1 block w-full p-3 text-xs outline-none rounded bg-[#F8F8F8]
                  ${errors.email ? "border border-red-500" : "border border-gray-300"}`}
              />
              {errors.email && (
                <p className="text-red-500 text-[10px] mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          {/* PHONE */}
          <div className="w-auto">
            <label className="block text-gray-700 text-xs">Phone Number</label>
            <input
              type="tel"
              name="phoneNo"
              value={userDetails.phoneNo}
              onChange={handleChange}
              placeholder="Enter mobile number"
              className={`mt-1 block w-[48%] p-3 text-xs outline-none rounded bg-[#F8F8F8]
                ${errors.phoneNo ? "border border-red-500" : "border border-gray-300"}`}
            />
            {errors.phoneNo && (
              <p className="text-red-500 text-[10px] mt-1">{errors.phoneNo}</p>
            )}
          </div>

          {/* FOOTER */}
          <div className="flex items-center justify-between pt-2 border-t-2 border-t-gray-200 px-4">
            <Link to="/change-password" className="block text-gray-700 text-xs">
              Change Password
            </Link>

            <ButtonWithLoader
              loading={loading}
              type="submit"
              buttonText="Update"
              width="w-[100px]"
            />
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProfile;
